import { useEffect, useState, useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import debounce from 'lodash.debounce';
import { Button, DatePicker, Form, Input, message, Modal, Select, Spin, Upload } from 'antd';
import categoriesApi from '@/api/categories';
import settingsApi from '@/api/settings';
import { categoryType } from '@/store/stateSlice';
import { disabledDate } from '@/utils/publicMethod';
import { calendarIcon, searchIcon } from '@/constants/static';

const MAX_QUESTIONS_COUNT = 10;
const defaultFormValues = {
    categoryName: '',
    categoryPosition: undefined as number | undefined,
    categoryDesc: '',
};

const isPersistedQuestionId = (id: unknown) => {
    if (id == null) return false;
    return /^\d+$/.test(String(id));
};

const normalizeQuestionList = (questionList: any[] = []) => {
    return questionList.map((q, index) => {
        if (typeof q === 'string') {
            return {
                clientKey: `draft-${index}`,
                questionId: null,
                question: q,
                deleted: false,
                isAdd: true,
            };
        }
        const persisted = isPersistedQuestionId(q.questionId);
        return {
            ...q,
            clientKey: persisted ? String(q.questionId) : `draft-${index}`,
            questionId: persisted ? q.questionId : null,
            question: q.question || '',
            deleted: !!q.deleted,
            isAdd: !persisted,
        };
    });
};

const buildUpdateQuestionPayload = (questions: any[]) => {
    return questions
        .map(q => ({
            questionId: q.isAdd || !isPersistedQuestionId(q.questionId) ? null : q.questionId,
            question: q.question?.trim() ?? '',
            deleted: !!q.deleted,
        }))
        .filter(q => q.deleted || q.question);
};

const normalizeQuestionsForList = (questionList: any[] = []) => {
    return questionList
        .filter(q => !q.deleted && q.question)
        .map(q => ({
            questionId: q.questionId,
            question: q.question,
        }));
};

const getVisibleQuestions = (questionList: any[] = []) => {
    return questionList.filter(q => !q.deleted && q.question?.trim());
};

const Categories = () => {
    const [form] = Form.useForm();
    const formValues = Form.useWatch([], form);
    const [categoryId, setCategoryId] = useState('');
    const [openModal, setOpenModal] = useState(false);
    const [showFormModal, setShowFormModal] = useState(false);
    const [editingCategory, setEditingCategory] = useState<categoryType | null>(null);
    const [categoryIcon, setCategoryIcon] = useState('');
    const [questions, setQuestions] = useState<any[]>([]);
    const [positions, setPositions] = useState<{ value: number; label: number; disabled: boolean }[]>([]);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const { RangePicker } = DatePicker;
    const [loading, setLoading] = useState(false);
    const [category, setCategory] = useState<categoryType[]>([]);
    const [listParams, setListParams] = useState({
        startTime: '',
        endTime: '',
        keyword: '',
    });
    const renderQuestion = useMemo(() => questions.filter(q => !q?.deleted), [questions]);
    const inputChange = e => {
        debouncedSave({ ...listParams, keyword: e.target.value });
    };
    const debouncedSave = useCallback(
        debounce(nextValue => setListParams(nextValue), 1500),
        []
    );
    const getList = async () => {
        setLoading(true);
        try {
            const res = await categoriesApi.getCategoryList(listParams);
            setCategory(res.data);
        } catch (error: any) {
            message.error(error);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        getList();
    }, [listParams]);
    const dateOnChange = date => {
        setListParams({
            ...listParams,
            startTime: date ? dayjs(`${dayjs(date[0]).format('YYYY-MM-DD')} 00:00:00`).toISOString() : '',
            endTime: date ? dayjs(`${dayjs(date[1]).format('YYYY-MM-DD')} 23:59:59`).toISOString() : '',
        });
    };
    const getMaxPosition = async (isEdit: boolean) => {
        try {
            const res = await categoriesApi.maxPosition();
            const max = isEdit ? res.data : res.data + 1;
            const options: { value: number; label: number; disabled: boolean }[] = [];
            for (let index = 1; index < max; index++) {
                options.push({ value: index, label: index, disabled: false });
            }
            setPositions(options);
        } catch (error: any) {
            message.error(error);
        }
    };

    const openAddModal = () => {
        setEditingCategory(null);
        setCategoryIcon('');
        setQuestions([]);
        form.resetFields();
        form.setFieldsValue(defaultFormValues);
        getMaxPosition(false);
        setShowFormModal(true);
    };

    const openEditModal = (item: categoryType) => {
        setEditingCategory(item);
        setCategoryIcon(item.categoryIcon || '');
        setQuestions(normalizeQuestionList(item.questionList || []));
        form.resetFields();
        form.setFieldsValue({
            categoryName: item.categoryName,
            categoryPosition: item.categoryPosition,
            categoryDesc: item.categoryDesc,
            categoryIcon: item.categoryIcon,
        });
        getMaxPosition(true);
        setShowFormModal(true);
    };

    const closeFormModal = () => {
        setShowFormModal(false);
        setEditingCategory(null);
        setCategoryIcon('');
        setQuestions([]);
        form.resetFields();
    };

    const closeModal = () => {
        setOpenModal(false);
    };
    const deleteCategory = async () => {
        try {
            await categoriesApi.deleteCategory(categoryId);
            getList();
            setCategoryId('');
            setOpenModal(false);
        } catch (error: any) {
            message.error(error);
        }
    };
    const upload = async (file: File) => {
        setUploading(true);
        const formData = new FormData();
        formData.append('file', file);
        try {
            const res = await settingsApi.uploadFile(formData);
            setCategoryIcon(res.data);
            form.setFieldsValue({ categoryIcon: res.data });
            form.validateFields(['categoryIcon']).catch(() => undefined);
        } catch (error: any) {
            message.error(error);
        } finally {
            setUploading(false);
        }
    };

    const beforeUpload = (file: File) => {
        const isLt5M = file.size / 1024 / 1024 < 5;
        if (!isLt5M) {
            message.error('Image must smaller than 5MB!');
            return false;
        }
        upload(file);
        return false;
    };

    const validateIcon = () => {
        if (categoryIcon) {
            return Promise.resolve();
        }
        return Promise.reject(new Error('Please upload an icon'));
    };

    const handleQuestionInput = (e, clientKey) => {
        setQuestions(state => state.map(q => (q.clientKey === clientKey ? { ...q, question: e.target.value } : q)));
    };

    const handleQuestionDelete = clientKey => {
        setQuestions(state => state.map(q => (q.clientKey === clientKey ? { ...q, deleted: true } : q)));
    };

    const handleQuestionAdd = () => {
        if (renderQuestion.length >= MAX_QUESTIONS_COUNT) return;
        setQuestions(state => [
            ...state,
            { clientKey: `draft-${Date.now()}`, questionId: null, question: '', deleted: false, isAdd: true },
        ]);
    };

    const saveCategory = async (values: any) => {
        setSaving(true);
        const params = {
            categoryName: values.categoryName,
            categoryPosition: values.categoryPosition,
            categoryDesc: values.categoryDesc,
            categoryIcon,
            questionList: [{}] as any[],
        };
        try {
            if (editingCategory) {
                params.questionList = buildUpdateQuestionPayload(questions);
                await categoriesApi.updateCategory(editingCategory.categoryId, params);
                setCategory(state =>
                    state.map(item =>
                        item.categoryId === editingCategory.categoryId
                            ? {
                                  ...item,
                                  categoryName: values.categoryName,
                                  categoryPosition: values.categoryPosition,
                                  categoryDesc: values.categoryDesc,
                                  categoryIcon,
                                  questionList: normalizeQuestionsForList(params.questionList),
                              }
                            : item
                    )
                );
                message.success('Category updated');
            } else {
                params.questionList = getVisibleQuestions(questions).map(q => q.question.trim());
                await categoriesApi.addCategory(params);
                message.success('Category created');
            }
            closeFormModal();
            getList();
        } catch (error: any) {
            message.error(error);
        } finally {
            setSaving(false);
        }
    };

    const saveDisabled =
        !categoryIcon || !formValues?.categoryName || !formValues?.categoryPosition || !formValues?.categoryDesc;

    return (
        <div className="page-main Categories">
            <div className="page-title flex-between">
                Market Categories
                <Button
                    className="flex-center"
                    style={{ height: 46, minWidth: 123, padding: '0 16px', borderRadius: 8 }}
                    type="primary"
                    onClick={openAddModal}
                >
                    <img src={require('@/assets/images/add-icon.png')} alt="" /> Add new
                </Button>
            </div>
            <div className="select-bar">
                <RangePicker
                    className="search-input"
                    placeholder={['dd/mm/yyyy', 'dd/mm/yyyy']}
                    suffixIcon={calendarIcon}
                    separator={'-'}
                    format={'DD/MM/YYYY'}
                    disabledDate={disabledDate}
                    onChange={dateOnChange}
                />
                <Input
                    className="search-input"
                    placeholder="Search by name"
                    onChange={inputChange}
                    prefix={searchIcon}
                />
            </div>
            {loading ? (
                <div className="data_loading flex-center">
                    <Spin />
                </div>
            ) : category.length ? (
                <div className="Categories">
                    {category.map(c => {
                        return (
                            <div className="Categories-item" key={c.categoryId}>
                                <div className="Categories-item-left">
                                    <div className="item-num">{c.categoryPosition}</div>
                                    <img className="item-img" src={c.categoryIcon} alt="Group" />
                                    <div className="item-base">
                                        <div className="item-name">{c.categoryName}</div>
                                        <div className="item-taskers">
                                            {c.assigneeCount} {Number(c.assigneeCount) > 1 ? 'Taskers' : 'Tasker'}
                                        </div>
                                    </div>
                                </div>
                                <div className="Categories-item-right">
                                    <img
                                        src={require('@/assets/images/edit-icon.png')}
                                        alt="edit-icon"
                                        onClick={() => openEditModal(c)}
                                    />
                                    <img
                                        src={require('@/assets/images/delete-icon.png')}
                                        alt="delete-icon"
                                        onClick={() => {
                                            setOpenModal(true);
                                            setCategoryId(c.categoryId);
                                        }}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="no-data flex-center">No data</div>
            )}
            <Modal
                width={680}
                className="category-form-modal"
                maskClosable={false}
                centered={true}
                closable={false}
                open={showFormModal}
                onCancel={closeFormModal}
                footer={null}
            >
                <div className="modal-title">{editingCategory ? 'Edit category' : 'Add new category'}</div>
                <Form form={form} layout="vertical" onFinish={saveCategory} validateTrigger="onBlur" autoComplete="off">
                    <div className="category-modal-stack">
                        <section className="category-modal-section">
                            <h3>Basic details</h3>
                            <div className="category-basic-grid">
                                <Form.Item
                                    label="Name"
                                    name="categoryName"
                                    rules={[{ required: true, message: 'Please enter name' }]}
                                >
                                    <Input placeholder="Category name" maxLength={50} />
                                </Form.Item>
                                <Form.Item
                                    label="Position"
                                    name="categoryPosition"
                                    rules={[{ required: true, message: 'Please select position' }]}
                                >
                                    <Select
                                        placeholder="Select position"
                                        suffixIcon={
                                            <img
                                                src={require('@/assets/images/select-icon.png')}
                                                alt=""
                                                style={{ width: 24, height: 24 }}
                                            />
                                        }
                                        options={positions}
                                    />
                                </Form.Item>
                            </div>
                        </section>
                        <section className="category-modal-section">
                            <h3>Category icon</h3>
                            <Form.Item
                                name="categoryIcon"
                                rules={[{ validator: validateIcon }]}
                                className="category-icon-field"
                            >
                                <div className="category-modal-upload">
                                    {categoryIcon ? (
                                        <img className="category-modal-photo" src={categoryIcon} alt="" />
                                    ) : (
                                        <div className="category-modal-placeholder">
                                            <img src={require('@/assets/images/upload-img.png')} alt="" />
                                        </div>
                                    )}
                                    <div>
                                        <Upload
                                            accept="image/*"
                                            maxCount={1}
                                            beforeUpload={beforeUpload}
                                            showUploadList={false}
                                        >
                                            <span className="category-upload-link">Click to upload</span>
                                        </Upload>
                                        <div className="category-upload-help">Max. file size 5MB</div>
                                    </div>
                                </div>
                            </Form.Item>
                        </section>
                        <section className="category-modal-section">
                            <h3>Questions</h3>
                            <p>Questions that prompt a Yes or No answer relative to this category</p>
                            <div className="category-modal-questions">
                                {renderQuestion.map((q, index) => (
                                    <div key={q.clientKey ?? index} className="category-modal-question">
                                        <span>No.{index + 1}</span>
                                        <Input
                                            placeholder="eg. I have my own TV Bracket"
                                            maxLength={100}
                                            value={q.question}
                                            onChange={e => handleQuestionInput(e, q.clientKey)}
                                        />
                                        <img
                                            src={require('@/assets/images/delete-icon.png')}
                                            alt="delete"
                                            onClick={() => handleQuestionDelete(q.clientKey)}
                                        />
                                    </div>
                                ))}
                                {renderQuestion.length < MAX_QUESTIONS_COUNT && (
                                    <button type="button" className="category-add-question" onClick={handleQuestionAdd}>
                                        <span>+</span>
                                        <span>Add question</span>
                                    </button>
                                )}
                            </div>
                        </section>
                        <section className="category-modal-section category-notes-section">
                            <h3>Notes</h3>
                            <Form.Item label="Category notes" name="categoryDesc">
                                <Input.TextArea
                                    maxLength={500}
                                    autoSize={{ minRows: 4, maxRows: 7 }}
                                    placeholder="Add notes for this category"
                                />
                            </Form.Item>
                        </section>
                    </div>
                    <div className="category-modal-actions">
                        <Button className="cancel-btn" onClick={closeFormModal}>
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={saving || uploading}
                            disabled={saveDisabled || uploading}
                        >
                            Save
                        </Button>
                    </div>
                </Form>
            </Modal>
            <Modal
                width={408}
                className="tasker-category-modal tasker-category-delete-modal"
                maskClosable={false}
                centered={true}
                closable={false}
                open={openModal}
                onCancel={closeModal}
                footer={null}
            >
                <div className="modal-title">Delete Category</div>
                <div className="modal-tip">Are you sure you want to delete this?</div>
                <div className="flex-center" style={{ marginTop: 24, columnGap: 16 }}>
                    <Button style={{ width: 132, height: 44 }} onClick={closeModal} className="cancel-btn">
                        Cancel
                    </Button>
                    <Button style={{ width: 132, height: 44 }} danger type="primary" onClick={deleteCategory}>
                        Yes, delete
                    </Button>
                </div>
            </Modal>
        </div>
    );
};
export default Categories;
